import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Plus, Pencil, Trash2, X, Check, AlertTriangle, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "codes", label: "Codes erreur", icon: AlertTriangle },
  { key: "docs",  label: "Documentation", icon: BookOpen },
];

const SEVERITIES = ["critical", "warning", "info"];
const CATEGORIES = ["heating", "flow", "sensor", "electric", "error"];
const DOC_TYPES = ["manual", "troubleshoot", "exploded_view", "schematic"];

export default function AdminPro() {
  const [tab, setTab] = useState("codes");
  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="admin-pro-page">
      <div className="eyebrow">Admin · Espace Pro</div>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Base de connaissances Pro</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Ajoutez, éditez ou supprimez les codes erreur et documents techniques exposés dans l'espace Pro.
      </p>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-black/10">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-pro-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 font-medium text-sm transition-colors ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "codes" ? <CodesManager /> : <DocsManager />}
      </div>
    </div>
  );
}

// ---------------------------- Codes -----------------------------------------

const EMPTY_CODE = {
  code: "", brand: "balboa", category: "heating", severity: "warning",
  title: "", description: "", action_summary: "",
};

function CodesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item or 'new'

  const load = () => {
    setLoading(true);
    api.get("/pro/error-codes?limit=200")
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing({ isNew: true, ...EMPTY_CODE });
  const startEdit = (i) => setEditing({ isNew: false, ...i });
  const cancel = () => setEditing(null);

  const save = async (form) => {
    try {
      if (form.isNew) {
        await api.post("/admin/pro/error-codes", form);
        toast.success("Code créé");
      } else {
        await api.put(`/admin/pro/error-codes/${form.id}`, form);
        toast.success("Code mis à jour");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce code ?")) return;
    try {
      await api.delete(`/admin/pro/error-codes/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{items.length} code(s) référencé(s)</p>
        <Button onClick={startNew} className="rounded-full" data-testid="add-error-code-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau code
        </Button>
      </div>

      {editing && <CodeForm value={editing} onSave={save} onCancel={cancel} />}

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((i) => (
            <Card key={i.id} className="p-4 border-black/10" data-testid={`admin-code-${i.id}`}>
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-slate-950 text-amber-400 px-2 py-1 text-xs font-black font-mono">{i.code}</span>
                    <span className="text-xs capitalize text-muted-foreground">{i.brand}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                      i.severity === "critical" ? "bg-red-100 text-red-700"
                      : i.severity === "warning" ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-700"
                    }`}>{i.severity}</span>
                  </div>
                  <h4 className="mt-2 text-sm font-semibold truncate">{i.title}</h4>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => startEdit(i)} data-testid={`edit-code-${i.id}`} className="p-1.5 rounded-md hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(i.id)} data-testid={`delete-code-${i.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CodeForm({ value, onSave, onCancel }) {
  const [form, setForm] = useState(value);
  useEffect(() => setForm(value), [value]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Card className="p-6 border-primary/30 bg-primary/5 mb-4" data-testid="code-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><Label>Code *</Label><Input value={form.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} data-testid="code-form-code" /></div>
        <div><Label>Marque *</Label><Input value={form.brand} onChange={(e) => upd("brand", e.target.value.toLowerCase())} data-testid="code-form-brand" /></div>
        <div><Label>Catégorie</Label><select value={form.category} onChange={(e) => upd("category", e.target.value)} className="h-10 w-full rounded-md border border-black/10 bg-background px-3 text-sm" data-testid="code-form-category">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        <div><Label>Sévérité</Label><select value={form.severity} onChange={(e) => upd("severity", e.target.value)} className="h-10 w-full rounded-md border border-black/10 bg-background px-3 text-sm" data-testid="code-form-severity">{SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
      </div>
      <div className="mt-3"><Label>Titre *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="code-form-title" /></div>
      <div className="mt-3"><Label>Description *</Label><Textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3} className="resize-none" data-testid="code-form-desc" /></div>
      <div className="mt-3"><Label>Actions recommandées *</Label><Textarea value={form.action_summary} onChange={(e) => upd("action_summary", e.target.value)} rows={3} className="resize-none" data-testid="code-form-actions" /></div>
      <div className="mt-4 flex gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} data-testid="code-form-cancel"><X className="h-4 w-4 mr-2" />Annuler</Button>
        <Button onClick={() => onSave(form)} data-testid="code-form-save"><Check className="h-4 w-4 mr-2" />Enregistrer</Button>
      </div>
    </Card>
  );
}

// ---------------------------- Docs ------------------------------------------

const EMPTY_DOC = {
  brand: "balboa", model: "", title: "", doc_type: "manual",
  summary: "", external_url: "", thumbnail_url: "", tags: [],
};

function DocsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/pro/docs?limit=200")
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    try {
      const payload = { ...form, tags: (typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags) };
      if (form.isNew) {
        await api.post("/admin/pro/docs", payload);
        toast.success("Document créé");
      } else {
        await api.put(`/admin/pro/docs/${form.id}`, payload);
        toast.success("Document mis à jour");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/admin/pro/docs/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">{items.length} document(s) référencé(s)</p>
        <Button onClick={() => setEditing({ isNew: true, ...EMPTY_DOC })} className="rounded-full" data-testid="add-doc-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouveau document
        </Button>
      </div>

      {editing && <DocForm value={editing} onSave={save} onCancel={() => setEditing(null)} />}

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((i) => (
            <Card key={i.id} className="p-4 border-black/10" data-testid={`admin-doc-${i.id}`}>
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-bold uppercase">{i.doc_type}</span>
                    <span className="text-xs capitalize text-muted-foreground">{i.brand}{i.model && ` · ${i.model}`}</span>
                  </div>
                  <h4 className="mt-2 text-sm font-semibold truncate">{i.title}</h4>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing({ isNew: false, ...i, tags: i.tags.join(", ") })} data-testid={`edit-doc-${i.id}`} className="p-1.5 rounded-md hover:bg-muted"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(i.id)} data-testid={`delete-doc-${i.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DocForm({ value, onSave, onCancel }) {
  const [form, setForm] = useState(value);
  useEffect(() => setForm(value), [value]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Card className="p-6 border-primary/30 bg-primary/5 mb-4" data-testid="doc-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Marque *</Label><Input value={form.brand} onChange={(e) => upd("brand", e.target.value.toLowerCase())} data-testid="doc-form-brand" /></div>
        <div><Label>Modèle</Label><Input value={form.model} onChange={(e) => upd("model", e.target.value)} data-testid="doc-form-model" /></div>
        <div><Label>Type</Label><select value={form.doc_type} onChange={(e) => upd("doc_type", e.target.value)} className="h-10 w-full rounded-md border border-black/10 bg-background px-3 text-sm" data-testid="doc-form-type">{DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
      </div>
      <div className="mt-3"><Label>Titre *</Label><Input value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="doc-form-title" /></div>
      <div className="mt-3"><Label>Résumé</Label><Textarea value={form.summary} onChange={(e) => upd("summary", e.target.value)} rows={3} className="resize-none" data-testid="doc-form-summary" /></div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>URL externe</Label><Input value={form.external_url} onChange={(e) => upd("external_url", e.target.value)} data-testid="doc-form-url" /></div>
        <div><Label>Tags (séparés par des virgules)</Label><Input value={form.tags} onChange={(e) => upd("tags", e.target.value)} data-testid="doc-form-tags" /></div>
      </div>
      <div className="mt-4 flex gap-2 justify-end">
        <Button variant="ghost" onClick={onCancel} data-testid="doc-form-cancel"><X className="h-4 w-4 mr-2" />Annuler</Button>
        <Button onClick={() => onSave(form)} data-testid="doc-form-save"><Check className="h-4 w-4 mr-2" />Enregistrer</Button>
      </div>
    </Card>
  );
}
